import React, { useState } from 'react';
import { parseShippingMethods, addBusinessDays, calculateShippingPrice } from '../../util/dates';
import DeliveryConditionsPanel from './DeliveryConditionsPanel';
import { NamedLink } from '../../components';
import deliveryIcon from '../../assets/delivery-icon.png';
import css from './ListingPage.module.css';

const SectionDeliveryInfo = props => {
  const { listing, price, intl, onOpenLoyaltyPanel } = props;

  const [conditionsPanelOpen, setConditionsPanelOpen] = useState(false);

  const shippingMethodsRaw = listing?.attributes?.publicData?.shippingMethods;
  const shippingMethods = parseShippingMethods(shippingMethodsRaw);

  if (!shippingMethods || shippingMethods.length === 0) {
    return null;
  }

  const delays = shippingMethods.map(m => parseInt(m.delay, 10)).filter(d => d > 0);
  if (delays.length === 0) {
    return null;
  }

  const shortestDelay = Math.min(...delays);
  const fastestMethod = shippingMethods.find(m => parseInt(m.delay, 10) === shortestDelay);
  const listingPriceAmount = price?.amount || 0;
  const currency = price?.currency || 'EUR';
  const shippingPrice = fastestMethod ? calculateShippingPrice(fastestMethod, 1) : 0;
  const franco = fastestMethod?.franco || 0;
  const isFreeShipping =
    shippingPrice === 0 || (typeof franco === 'number' && franco > 0 && listingPriceAmount >= franco);
  const shippingLabel = isFreeShipping
    ? 'Livraison gratuite'
    : `${intl.formatNumber(shippingPrice / 100, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} de livraison`;

  const deliveryDate = addBusinessDays(new Date(), shortestDelay);
  const formattedDate = intl.formatDate(deliveryDate, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className={css.mobileDeliveryInfoSection}>
      <div className={css.deliveryContainer}>
        <div
          className={css.deliveryMethodsDisplay}
          role="button"
          tabIndex={0}
          onClick={() => setConditionsPanelOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setConditionsPanelOpen(true);
            }
          }}
        >
          <div className={css.deliveryTextContainer}>
            <div className={css.deliveryRow}>
              <img src={deliveryIcon} alt="Livraison" className={css.deliveryIcon} />
              <p className={css.deliveryText}>
                <strong>Chez vous le {formattedDate}</strong>
                {` (${shippingLabel})`}
              </p>
            </div>
            <div className={css.deliveryRow}>
              <svg className={css.deliveryIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M4 8L3.64645 8.35355L3.29289 8L3.64645 7.64645L4 8ZM9 19.5C8.72386 19.5 8.5 19.2761 8.5 19C8.5 18.7239 8.72386 18.5 9 18.5L9 19.5ZM8.64645 13.3536L3.64645 8.35355L4.35355 7.64645L9.35355 12.6464L8.64645 13.3536ZM3.64645 7.64645L8.64645 2.64645L9.35355 3.35355L4.35355 8.35355L3.64645 7.64645ZM4 7.5L14.5 7.5L14.5 8.5L4 8.5L4 7.5ZM14.5 19.5L9 19.5L9 18.5L14.5 18.5L14.5 19.5ZM20.5 13.5C20.5 16.8137 17.8137 19.5 14.5 19.5L14.5 18.5C17.2614 18.5 19.5 16.2614 19.5 13.5L20.5 13.5ZM14.5 7.5C17.8137 7.5 20.5 10.1863 20.5 13.5L19.5 13.5C19.5 10.7386 17.2614 8.5 14.5 8.5L14.5 7.5Z" fill="#222222"/>
              </svg>
              <p className={css.deliveryText}>14 jours satisfait ou remboursé</p>
            </div>
            <div className={css.deliveryRow}>
              <svg className={css.deliveryIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect fill="none" stroke="#020202" strokeMiterlimit="10" strokeWidth="1" x="6.27" y="5.32" width="11.45" height="15.27" rx="5.73" />
                <path fill="none" stroke="#020202" strokeMiterlimit="10" strokeWidth="1" d="M17.73,9.14h1.91A2.86,2.86,0,0,1,22.5,12v1.91a2.86,2.86,0,0,1-2.86,2.86H17.73a0,0,0,0,1,0,0V9.14A0,0,0,0,1,17.73,9.14Z" />
                <path fill="none" stroke="#020202" strokeMiterlimit="10" strokeWidth="1.1" d="M1.5,9.14H3.41A2.86,2.86,0,0,1,6.27,12v1.91a2.86,2.86,0,0,1-2.86,2.86H1.5a0,0,0,0,1,0,0V9.14A0,0,0,0,1,1.5,9.14Z" transform="translate(7.77 25.91) rotate(180)" />
                <path fill="none" stroke="#020202" strokeMiterlimit="10" strokeWidth="1.1" d="M4.36,9.14h0A7.64,7.64,0,0,1,12,1.5h0a7.64,7.64,0,0,1,7.64,7.64h0" />
                <path fill="none" stroke="#020202" strokeMiterlimit="10" strokeWidth="1.1" d="M19.64,16.77v1a4.78,4.78,0,0,1-4.78,4.77" />
              </svg>
              <div className={css.deliverySupportInline}>
                <p className={css.deliveryText}>Service client Neokasa</p>
                <svg
                  viewBox="0 0 36 36"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  role="img"
                  className={css.deliveryFlagFrEnd}
                  preserveAspectRatio="xMidYMid meet"
                >
                  <path fill="#f53e28" d="M36 27a4 4 0 0 1-4 4h-8V5h8a4 4 0 0 1 4 4v18z" />
                  <path fill="#68abee" d="M4 5a4 4 0 0 0-4 4v18a4 4 0 0 0 4 4h8V5H4z" />
                  <path fill="#ffffff" d="M12 5h12v26H12z" />
                </svg>
                <a className={css.deliveryText}>
                  Appelez-nous au 01 89 48 06 12
                </a>
              </div>
            </div>
          </div>
          <div className={css.deliveryNoteContainer}>
            <p className={css.deliveryNote}>
              <button
                type="button"
                className={css.deliveryConditionsLink}
                onClick={e => {
                  e.stopPropagation();
                  setConditionsPanelOpen(true);
                }}
              >
                Voir les conditions de livraison, retour et garantie
              </button>
            </p>
          </div>
        </div>
        {price && price.amount > 0 ? (() => {
          const points = Math.floor(price.amount / 100);
          const remaining = Math.max(0, 500 - points);
          return (
            <div className={css.deliveryNoteContainer}>
              <p className={css.deliveryNote}>
                {points >= 500 ? (
                  <>Vous cumulez <strong>{points} point{points > 1 ? 's' : ''}</strong> fidélité et bénéficiez déjà d'un avantage exclusif !</>
                ) : (
                  <>Vous cumulez <strong>{points} point{points > 1 ? 's' : ''}</strong> fidélité. Plus que <strong>{remaining} points</strong> pour bénéficier d'un avantage exclusif !</>
                )}
              </p>
              <p className={css.deliveryNote}>
                🎁{' '}
                {onOpenLoyaltyPanel ? (
                  <button
                    type="button"
                    className={css.deliveryConditionsLink}
                    onClick={onOpenLoyaltyPanel}
                  >
                    En savoir plus sur notre programme fidélité
                  </button>
                ) : (
                  <NamedLink name="ProgrammeFidelitePage" className={css.deliveryConditionsLink}>
                    En savoir plus sur notre programme fidélité
                  </NamedLink>
                )}
              </p>
            </div>
          );
        })() : null}
      </div>
      <DeliveryConditionsPanel
        isOpen={conditionsPanelOpen}
        onClose={() => setConditionsPanelOpen(false)}
        listing={listing}
        price={price}
        intl={intl}
      />
    </div>
  );
};

export default SectionDeliveryInfo;

